<template lang="pug">
	.mraid-sdk-tutorial(v-if="showed")
		.card(v-pseudo-modal-name="'mraid-sdk-tutorial'")
			.card-header
				.header-title
					i.fa.fa-question-circle
					span MraidSDK documentation
				.close-button(@click="hide()")
					i.fa.fa-close
			.card-content
				.chapter-info(v-html="'MraidSDK is a javascript code that is automatically connected to all creatives inside Dashboard and provides global access to the MraidSDK object. Loading and connecting MraidSDK manually is not practiced, since we need to be able to change and improve it often. This object and its methods will allow your game to more tightly integrate with the functionality and such features of Dashboard as replacing images and sounds, connecting and setting the end screen, setting the transition to the target url after a certain number of seconds or clicks, adding a loading screen and many other useful settings.'")
				.chapter-info(v-html="'MraidSDK will be added before your javascript code. And you should start using it as soon as possible. You can use the code like <code>if (window.MraidSDK) MraidSDK.callMethod(); else callYourCode();</code> multiple times to support both Dashboard launch and local launch of your game. It is recommended to add all MraidSDK integration features. In this case Dashboard settings and features will work correctly in your game.'")
				.tutorial-chapter-text
					h5
						.title(v-html="`MraidSDK.track('<b>Code Loaded</b>')`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.track('Code Loaded');
					p.description This call is used to track the successful launch of your game. Use it before assets start loading, but after the main code of your game is loaded and initialized. The number of such events in relation to the number of ad launches will indicate the percentage of technical problems, errors in the game code.
					h5
						.title(v-html="`MraidSDK.track('<b>Assets Loaded</b>')`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.track('Assets Loaded');
					p.description This call is also used to track whether your game has successfully launched in the case of assets. Use it after the main assets have finished loading. If you use delayed loading of some assets, you can additionally call the "Delayed Assets Loaded" tracking:
					code if (window.MraidSDK) MraidSDK.track('Delayed Assets Loaded');
					p.description The number of such events in relation to the number of Code Loaded events can indicate the percentage of problems with loading game assets. Also it will indicate the average assets loading time in the stats.
					p.description Usually after this tracking MraidSDK will broadcast "Start Game" event (if creative visible to user and 'start-on' setting has default 'viewable' value).
					h5
						.title(v-html="`MraidSDK.<b>getSize</b>()`")
					code let size = window.MraidSDK ? MraidSDK.getSize() : {width: window.innerWidth, height: window.innerHeight};
					p.description Use this call to receive viewport size for your game canvas. This will allow you to switch between window.innerWidth/innerHeight, mraid.getSize(), screen.width/height and similar methods of ad networks SDK just inside Dashboard, without having to fix your game code for each ad network.
					h5
						.title(v-html="`MraidSDK.<b>getMessage</b>()`")
					code if (window.MraidSDK) yourGameTextObject.setText(MraidSDK.getMessage('TEXT 1')); else yourGameTextObject.setText(Settings.Localization.en['TEXT 1'].text);
					p.description Use this method to receive text messages from Settings.Localization object and made localization in your creative. This method returns message according to current locale. Locale can be controlled via Dashboard in the Localization section of settings.
					h5
						.title(v-html="`MraidSDK.<b>getMessageObject</b>()`")
					code if (window.MraidSDK) yourGameTextObject.setStyles(MraidSDK.getMessageObject('TEXT 1')); else yourGameTextObject.getMessageObject(Settings.Localization.en['TEXT 1']);
					p.description The same like getMessage, but returns all message object, including styles.
					h5
						.title(v-html="`MraidSDK.on('<b>Start Game</b>', ...)`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.on('Start Game', () => { showGame(); MraidSDK.track('Game Starts'); }); else showGame();
					p.description This code will allow the Dashboard to control the moment the game showed to the user. This is important, because the game can be preloaded and waiting to be shown to the user. In the Dashboard, you can specify when to start the game: immediately after loading, after initialization of the ad network SDK, or when the player actually sees the game (this is the default value). Also, depending on the ad network, this behavior can be overridden.
					h5
						.title(v-html="`MraidSDK.track('<b>Game Starts</b>')`")
					code if (window.MraidSDK) MraidSDK.on('Creative Showed', () => { showGame(); MraidSDK.track('Game Starts'); }); else showGame();
					p.description Using this method you should notify MraidSDK that main game actually started. The countdown of the close button timer, calculations of clicks to redirect and clicks to ends screen will start after your "Game Starts" track. For example, you can have any intro screen before the game, in this case you should call "Game Starts" track after user is close this intro screen. In other cases you should call this method just inside "Creative Showed" callback.

					h5
						.title(v-html="`MraidSDK.<b>interaction</b>()`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.interaction(domEvent);
					p.description Call this method for every significant user action. For example, attacking an enemy, selecting a weapon, moving a puzzle tile, etc. Just clicks to nowhere do not need to be marked with this call. This will allow you to configure the transition to the store for a specified number of clicks and will help track player engagement in your game. It is also important to pass the DOM event object to this method, in this case we will be able to collect a heap map of clicks for statistics and analyze user behavior.
					h5
						.title(v-html="`MraidSDK.track('<b>Custom Event Name</b>')`")
					code if (window.MraidSDK) MraidSDK.track('Custom Event Name');
					p.description Using this method you can call any custom event depending on your game logic to improve tracking stats. Just put first parameter as a string with good title.
					h5
						.title(v-html="`MraidSDK.<b>playSound</b>()`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.playSound(assetName); else playSoundManually(assetName);
					p.description Call this method to play all sounds in the game. It will automatically check if sound on or off and call event Play Sound if sound can be played. This method and event needed to control sound mute in various situation to support ad networks requirements.
					p.description This method should be used together with "Play Sound" event.
					h5
						.title(v-html="`MraidSDK.on('<b>Play Sound</b>', [asset, name])`")
					code if (window.MraidSDK) MraidSDK.on("Play Sound", (asset, name) => { playSoundByYourGameEngineMethods(asset.sound); });
					p.description Use this event to play sounds in the game. It can be called after each your MraidSDK.playSound calls, in case if sound is on.
					p.description Also dashboard allow user to add new sound assets and choose on which event play this sound. If you want to support this feature just subscribe on this event and play sounds.
					h5
						.title(v-html="`MraidSDK.on('<b>Stop Sound</b>', [asset, name])`")
					code if (window.MraidSDK) MraidSDK.on('Stop Sound', (asset) => { ... stop playing sound asset ... });
					p.description Dashboard also allow user to choose on which event stop playing sound. If you want to support this feature just subscribe on this event and stop sounds there.
					h5
						.title(v-html="`MraidSDK.on('<b>Muted</b>', ...)`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.on('Muted', () => { ... off sound ... });
					p.description This event needed to control sound mute in various situation to support ad networks requirements. This is mandatory if you have a sound in the game. But note that MraidSDK will not call Play Sound event if sound is off. So Muted event can be redundant.
					h5
						.title(v-html="`MraidSDK.on('<b>Unmuted</b>', ...)`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.on('Unmuted', () => { ... on sound ... });
					p.description This event needed to control sound unmute in various situation to support ad networks requirements. This is mandatory if you have a sound in the game. But note that MraidSDK will not call Play Sound event if sound is off. So Unmuted event can be redundant.
					h5
						.title(v-html="`MraidSDK.<b>isMuted</b>()`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.isMuted();
					p.description Call this method to determine whether the sound should currently be muted. This method can be redundant if you play all sounds via MraidSDK.playSound() method and use MraidSDK Play Sound event. It will check mute state automatically.
					h5
						.title(v-html="`MraidSDK.on('<b>Paused</b>', ...)`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.on('Paused', () => { ... pause rendering, animations, tickers ... });
					p.description You should pause all rendering and game mechanics if playable hidden from user
					h5
						.title(v-html="`MraidSDK.on('<b>Resumed</b>', ...)`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.on('Resumed', () => { ... resume rendering, animations, tickers ... });
					p.description You should resume all rendering and game mechanics if playable again viewable to user
					h5
						.title(v-html="`MraidSDK.<b>showEndScreen</b>()`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.showEndScreen('game completed'); else showEndScreen();
					p.description This method allows you to display the end screen. When the game has ended (or for other reasons) and you need to show the end screen, use this method instead of displaying your native end screen directly. This will allow us to redirect the player to the target url instead of showing the end screen, or show a custom end screen created inside the Dashboard, depending on the settings. The first parameter is a string indicating the reason for opening the end screen for statistics. It can be "game completed", or "game fail", or "start screen" (if the player can go to the end screen directly from the start screen without going through the game), or something else.
					h5
						.title(v-html="`MraidSDK.<b>hideEndScreen</b>()`")
					code if (window.MraidSDK) MraidSDK.hideEndScreen(); else hideEndScreen();
					p.description Use this method to hide the end screen instead of hiding it directly. This will allow you to gracefully close the end screen if you will use an end screen created in the Dashboard instead of your native end screen.
					h5
						.title(v-html="`MraidSDK.on('<b>Show Native End Screen</b>', ...)`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.on('Show Native End Screen', () => { ... show your end screen here ... });
					p.description Use this event to show your native end screen, that you developed inside your game. This is also needed to support custom end screens created in the Dashboard.
					h5
						.title(v-html="`MraidSDK.on('<b>Hide Native End Screen</b>', ...)`")
					code if (window.MraidSDK) MraidSDK.on('Hide Native End Screen', () => { ... hide your end screen here ... });
					p.description Use this event to hide your native end screen, that you have developed inside your game. This is also needed to support custom end screens created in the Dashboard.
					h5
						.title(v-html="`MraidSDK.<b>open</b>()`")
						i.fa.fa-exclamation-triangle.snippet
						.snippet mandatory
					code if (window.MraidSDK) MraidSDK.open('end screen'); else alert('Clicked successfully');
					p.description Use this method to open the target URL correctly depending on the ad network specifications. The first parameter is a string indicating where this method was called for statistics (please, use standard 'in game', 'banner', 'end screen', 'loading screen' names if it suitable for you, otherwise use custom name). Second parameter can be url address, by default it taken from "target-url" setting, but you can specify any url or the name of other setting from which MraidSDK will take url.
					h5
						.title(v-html="`MraidSDK.<b>close</b>()`")
					code if (window.MraidSDK) MraidSDK.close('in game'); else alert('Closed successfully');
					p.description Use this method to close the creative depending on the ad network specifications. The first parameter is a string indicating where this method was called for statistics. Usually this method is not needed in the game code. It will be called automatically from close button that will be created by MraidSDK or ad network SDK.
					h5
						.title(v-html="`MraidSDK.<b>isReplayAvailable</b>()`")
					code if (window.MraidSDK) if (MraidSDK.isRetryAvailable()) { ... display Retry button ... } else { ... don't display Retry button ... };
					p.description Use this method to understand if replay button can be displayed on the end screen. Dashboard have setting that control maximum count of replays. If your game have replay functionality you should use this method. Also use MraidSDK.track("Retry") to mark each replay.
					h5
						.title(v-html="`MraidSDK.on('<b>Start Replay</b>', ...)`")
					code if (window.MraidSDK) MraidSDK.on('Start Replay', () => { ... hide end screen by MraidSDK.hideEndScreen() and start main gameplay again ... });
					p.description Use this event to return user from end screen to the game if you have replay functionality in your game. This can be called from Dashboard configured end screens, not only from your end screens. So this is recommended to implement this method.
					h5
						.title(v-html="`MraidSDK.<b>processReplay</b>()`")
					code if (window.MraidSDK) {MraidSDK.processReplay();} else { ... immediately start main gameplay again ... }
					p.description Use this method as click handler on your Replay button (usually this button located on the end screen).
					h5
						.title(v-html="`MraidSDK.on('<b>Setting Changed</b>', ...)`")
					code if (window.MraidSDK) MraidSDK.on('Setting Changed', (name, value) => { ... dynamically apply setting change if possible ... });
					p.description Use this method to change custom settings of your game on the fly. This works when the game is being tested and configured inside the Dashboard. For example, if you created a tile color setting in a puzzle game, changing that setting (tile color) in the Dashboard UI will trigger this event. And you can immediately change the color of the tiles without having to reload the game. This makes it much easier to manage settings and make them apply in real time.
					h5
						.title(v-html="`MraidSDK.on('<b>Asset Changed</b>', ...)`")
					code if (window.MraidSDK) MraidSDK.on('Asset Changed', (name, props) => { ... apply asset change if possible ... });
					p.description Same as Setting Changed. When you change an asset in the Dashboard, this event will be triggered and you will be able to make changes in the game immediately.

					p.description

</template>

<script>

	import Screen 				from './../../mixins/screen'

	export default {
		name: "MraidSDKTutorial",
		mixins: [Screen],
		screen: {
			updateOn: [],
			events: {
				"Show MraidSDK Tutorial": function() {

					this.show();

				}
			}
		},
		data() {

			return {
				showed: false
			};

		},
		methods: {
			collectData() {

			}
		}
	}
</script>

<style lang="sass">

	.app > .mraid-sdk-tutorial
		position: absolute
		z-index: 2000
		left: 0
		top: 0
		right: 0
		bottom: 0
		background-color: rgba(0, 0, 0, 0.6)
		transition: opacity 0.3s ease-in-out

		.card-header
			display: flex
			flex-direction: row
			height: 60px

			.header-title
				width: auto
				margin-top: auto
				margin-bottom: auto
				font-size: 22px
				color: var(--warning)

				span
					padding-left: 10px

				i

			.close-button
				font-size: 22px
				color: var(--warning)
				opacity: 0.3
				cursor: pointer
				padding: 0 20px
				margin-left: auto
				margin-right: -20px

				&:hover
					opacity: 1

		.card
			width: 850px
			position: absolute
			left: 0
			right: 0
			top: 0
			bottom: 0
			margin: auto
			display: flex
			height: 460px
			box-shadow: 0 0 20px rgba(0, 0, 0, 0.8)
			border-top: 3px solid var(--warning)
			transition: transform 0.3s ease-in-out

			.card-content
				display: flex
				flex-direction: column
				height: 100%
				padding: 20px
				z-index: 2
				overflow: auto

				.caption

				.tutorial-chapter-text

					h5
						display: flex
						flex-direction: row
						align-items: center
						margin-top: 20px
						font-size: 1.1rem
						text-align: left
						font-weight: normal
						margin-bottom: 10px
						padding-bottom: 3px
						border-bottom: 1px solid var(--warning)
						user-select: text

					p
						margin-top: 0

					i
						margin-top: 3px
						margin-left: 10px
						margin-right: 5px

					.snippet
						font-size: 12px
						color: var(--primary)
						text-transform: uppercase
						user-select: none

					.number
						font-size: 13px
						user-select: none
						color: var(--secondary)
						align-self: end
						padding-bottom: 1px
						margin-right: 5px

				.chapter-info
					text-indent: 0px
					margin-bottom: 10px

				b
					font-weight: normal
					color: var(--warning)

				code
					display: block
					margin: 10px

	.tutorial.showed
		pointer-events: all
		opacity: 1

	.tutorial.hiding
		opacity: 0

		.card


</style>

